import { useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { purgeCandidate } from "@/api/tests-api";

interface PurgeCandidateDialogProps {
  candidateUserId: string;
  candidateUsername: string;
  candidateName: string | null;
  onClose: () => void;
  onPurged: () => void;
}

/**
 * Delete a candidate's assessments, recordings and audio.
 *
 * This is the only thing in the system that ever deletes an assessment
 * recording — the seven-day sweep that disposes of clinical dictation
 * deliberately cannot reach them, because here the audio IS the answer.
 * There is no undo and no backup to restore from.
 *
 * So the confirmation is typed, not a button. An "Are you sure?" gets
 * clicked through; typing the username makes the reader look at which
 * candidate they are about to erase. The backend enforces the same check,
 * so this is a courtesy rather than the guard.
 */
export function PurgeCandidateDialog({
  candidateUserId,
  candidateUsername,
  candidateName,
  onClose,
  onPurged,
}: PurgeCandidateDialogProps) {
  const { toast } = useToast();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = typed.trim() === candidateUsername;

  const go = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      const summary = await purgeCandidate(candidateUserId, typed.trim());
      toast({
        title: "Candidate data purged",
        description:
          `${summary.attempts} attempt(s), ${summary.recordings} recording(s) deleted. ` +
          (summary.account_deactivated
            ? "The account has been deactivated."
            : "The account was already inactive."),
      });
      onPurged();
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not purge this candidate.";
      toast({ title: detail, variant: "error" });
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      data-cy="purge-dialog"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-labelledby="purge-title"
        className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-theme-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="purge-title" className="text-base font-semibold text-foreground">
          Purge {candidateName || candidateUsername}?
        </h2>

        <p className="mt-2 text-sm text-foreground-muted">
          This permanently deletes every attempt, answer, score and voice
          recording for this candidate. Recordings cannot be recovered —
          they are deliberately excluded from the routine cleanup, so this
          is the only thing that removes them.
        </p>
        <p className="mt-2 text-sm text-foreground-muted">
          The case library is not affected. The account is deactivated
          rather than deleted, so the audit trail keeps resolving.
        </p>

        <label className="mt-4 block text-xs text-foreground-muted">
          Type <strong className="font-mono text-foreground">{candidateUsername}</strong>{" "}
          to confirm
          <input
            data-cy="purge-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="sk-input mt-1 font-mono"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-cy="btn-purge-cancel"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:border-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            data-cy="btn-purge-confirm"
            disabled={!matches || busy}
            onClick={() => {
              void go();
            }}
            className="rounded-md bg-status-urgent px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? "Purging…" : "Purge permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
